import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, BookOpen, Sparkles, Palette, Image as ImageIcon, Package,
  Settings, LogOut, Plus, Shield, Download, CreditCard, LifeBuoy, X, Zap,
  ChevronsLeft, ChevronsRight
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const groups = [
  {
    title: "Workspace",
    items: [
      { to: "/app", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
      { to: "/app/books", icon: BookOpen, label: "I miei ebook", testid: "nav-books" },
      { to: "/app/new", icon: Plus, label: "Crea ebook", testid: "nav-new" },
    ],
  },
  {
    title: "AI Studio",
    items: [
      { to: "/app/templates", icon: Palette, label: "Template", testid: "nav-templates" },
      { to: "/app/tools", icon: Sparkles, label: "AI Tools", testid: "nav-tools" },
      { to: "/app/covers", icon: ImageIcon, label: "Immagini AI", testid: "nav-covers" },
      { to: "/app/brand", icon: Package, label: "Brand Kit", testid: "nav-brand" },
      { to: "/app/exports", icon: Download, label: "Export", testid: "nav-exports" },
    ],
  },
  {
    title: "Account",
    items: [
      { to: "/app/billing", icon: CreditCard, label: "Abbonamento", testid: "nav-billing" },
      { to: "/app/usage", icon: Zap, label: "Utilizzo", testid: "nav-usage" },
      { to: "/app/settings", icon: Settings, label: "Impostazioni", testid: "nav-settings" },
      { to: "/support", icon: LifeBuoy, label: "Assistenza", testid: "nav-support", external: true },
      { to: "/app/help", icon: LifeBuoy, label: "Supporto", testid: "nav-help" },
    ],
  },
];

function BrandMark({ collapsed = false }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center shadow-[0_6px_18px_-4px_rgba(99,102,241,0.55)] shrink-0">
        <span className="text-white font-bold text-[15px] tracking-tight">B</span>
        <span className="absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 rounded-full bg-[#A78BFA] border-2 border-[#0C111B]"/>
      </div>
      {!collapsed && (
        <div className="leading-tight">
          <p className="font-bold text-[15px] tracking-tight text-white">BookForge</p>
          <p className="text-[10px] font-semibold text-[#A5B4FC] tracking-wider">AI STUDIO</p>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ open, onClose }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/me/wallet");
        if (alive) setWallet(r.data);
      } catch (_) {}
    };
    load();
    // Refresh wallet when AI insufficient-credits triggers OR every 20s
    const onCreditChange = () => load();
    window.addEventListener("bf:insufficient-credits", onCreditChange);
    window.addEventListener("bf:credits-updated", onCreditChange);
    const t = setInterval(load, 20000);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener("bf:insufficient-credits", onCreditChange);
      window.removeEventListener("bf:credits-updated", onCreditChange);
    };
  }, [loc.pathname]);

  const widthClass = collapsed ? "w-[76px]" : "w-64";

  const content = (
    <aside className={`flex flex-col ${widthClass} min-h-screen bg-[#0C111B] border-r border-white/[0.06] h-full transition-all duration-200`}>
      {/* Brand */}
      <div className="flex items-center justify-between px-4 pt-5 pb-4">
        <button data-testid="logo-home-btn" onClick={() => { nav("/app"); onClose?.(); }} className="flex items-center">
          <BrandMark collapsed={collapsed}/>
        </button>
        <button data-testid="sidebar-close-btn" onClick={onClose} className="lg:hidden text-slate-400 p-1.5 hover:text-white rounded-md">
          <X className="w-4 h-4"/>
        </button>
      </div>

      {/* Nav groups */}
      <nav className="flex flex-col flex-1 overflow-y-auto px-3 pb-3 gap-5">
        {groups.map((g) => (
          <div key={g.title}>
            {!collapsed && (
              <p className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{g.title}</p>
            )}
            <div className="flex flex-col gap-0.5">
              {g.items.map((it) => {
                const Icon = it.icon;
                const active = loc.pathname === it.to || (it.to !== "/app" && loc.pathname.startsWith(it.to));
                return (
                  <Link
                    key={it.to}
                    data-testid={it.testid}
                    to={it.to}
                    onClick={onClose}
                    title={collapsed ? it.label : undefined}
                    className={`group relative flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3"} py-2.5 rounded-lg text-sm transition-all
                      ${active
                        ? "bg-gradient-to-r from-[#6366F1]/18 to-[#8B5CF6]/8 text-white border border-[#6366F1]/25"
                        : "text-slate-400 hover:text-white hover:bg-white/[0.04] border border-transparent"}`}
                  >
                    {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-[#6366F1] to-[#8B5CF6]"/>}
                    <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-[#A5B4FC]" : "text-slate-500 group-hover:text-slate-300"}`}/>
                    {!collapsed && <span className="truncate">{it.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
        {user?.role === "admin" && (
          <Link
            data-testid="nav-admin"
            to="/admin"
            onClick={onClose}
            className={`flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3"} py-2.5 rounded-lg text-sm text-rose-300 hover:text-white hover:bg-rose-500/10 border border-rose-500/20`}
          >
            <Shield className="w-[18px] h-[18px]"/>
            {!collapsed && <span>Admin Panel</span>}
          </Link>
        )}
      </nav>

      {/* Credits widget */}
      <CreditsWidget wallet={wallet} collapsed={collapsed} onUpgrade={() => { nav("/app/billing"); onClose?.(); }}/>

      {/* User + collapse */}
      <div className="border-t border-white/[0.06] px-3 py-3">
        {!collapsed ? (
          <>
            <div className="flex items-center gap-3 p-2 rounded-xl bg-white/[0.03] border border-white/[0.05] mb-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center text-sm font-bold text-white shrink-0">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-white truncate">{user?.name || user?.email}</p>
                <p className="text-[10px] text-[#A5B4FC] uppercase tracking-wider font-semibold">{user?.plan || "free"}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors"
              >
                <LogOut className="w-3.5 h-3.5"/>Esci
              </button>
              <button
                data-testid="sidebar-collapse-btn"
                onClick={() => setCollapsed(true)}
                className="hidden lg:flex items-center justify-center px-2.5 py-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/[0.04]"
                title="Comprimi sidebar"
              >
                <ChevronsLeft className="w-4 h-4"/>
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center text-sm font-bold text-white">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <button
              data-testid="sidebar-expand-btn"
              onClick={() => setCollapsed(false)}
              className="hidden lg:flex items-center justify-center p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-white/[0.04]"
              title="Espandi"
            >
              <ChevronsRight className="w-4 h-4"/>
            </button>
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 text-slate-500 hover:text-red-300 rounded-md" title="Esci">
              <LogOut className="w-4 h-4"/>
            </button>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <>
      <div className="hidden lg:block">{content}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
          <div className="relative animate-[fadeInUp_0.2s_ease-out]">{content}</div>
        </div>
      )}
    </>
  );
}

function CreditsWidget({ wallet, collapsed, onUpgrade }) {
  if (!wallet) return null;
  const unlimited = !!wallet.unlimited;
  const total = (wallet.monthly_total || 0) + (wallet.extra_credits || 0);
  const remaining = wallet.remaining || 0;
  const pct = unlimited ? 100 : (total > 0 ? Math.min(100, Math.round((remaining / total) * 100)) : 0);
  const empty = !unlimited && remaining <= 0;
  const low = !unlimited && !empty && pct <= 20;
  const barClass = unlimited
    ? "bg-gradient-to-r from-emerald-400 to-cyan-400"
    : empty
      ? "bg-red-500/70"
      : low
        ? "bg-amber-400/80"
        : "bg-gradient-to-r from-[#6366F1] to-[#8B5CF6]";

  // Words
  const w = wallet.words || { monthly: 0, used: 0, remaining: 0 };
  const wTotal = w.monthly || 0;
  const wRem = w.remaining || 0;
  const wPct = unlimited ? 100 : (wTotal > 0 ? Math.min(100, Math.round((wRem / wTotal) * 100)) : 0);
  const wEmpty = !unlimited && wTotal > 0 && wRem <= 0;
  const wLow = !unlimited && !wEmpty && wTotal > 0 && wPct <= 20;
  const wBarClass = unlimited
    ? "bg-gradient-to-r from-emerald-400 to-cyan-400"
    : wEmpty ? "bg-red-500/70" : wLow ? "bg-amber-400/80" : "bg-gradient-to-r from-[#3B82F6] to-[#6366F1]";

  if (collapsed) {
    return (
      <button
        data-testid="sidebar-credits-chip"
        onClick={onUpgrade}
        className="mx-auto mb-2 flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/[0.04]"
        title={`${remaining}/${total} crediti · ${wRem}/${wTotal} parole`}
      >
        <Zap className={`w-4 h-4 ${unlimited ? "text-emerald-400" : empty ? "text-red-400" : "text-[#A5B4FC]"}`}/>
        <span className={`text-[10px] font-mono ${unlimited ? "text-emerald-400" : empty ? "text-red-400" : "text-slate-300"}`}>{unlimited ? "∞" : remaining}</span>
      </button>
    );
  }

  const ctaLabel = empty || wEmpty ? "Upgrade" : (low || wLow) ? "Upgrade" : "Gestisci piano";

  return (
    <div data-testid="sidebar-credits-widget" className="mx-3 mb-2 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
      {/* Credits */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
          <Zap className="w-3 h-3 text-[#A5B4FC]"/>Crediti AI
        </div>
        <span data-testid="sidebar-credits-count" className={`text-[10px] font-mono ${unlimited ? "text-emerald-400" : empty ? "text-red-400" : "text-slate-200"}`}>
          {unlimited ? "∞ Admin" : <>{remaining.toLocaleString()}<span className="text-slate-500">/{total.toLocaleString()}</span></>}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-white/[0.05] overflow-hidden mb-2">
        <div className={`h-full ${barClass} transition-all`} style={{ width: `${pct}%` }}/>
      </div>

      {/* Words */}
      <div className="flex items-center justify-between mb-1 mt-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
          <svg className="w-3 h-3 text-[#93C5FD]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h10M4 17h16"/></svg>
          Parole
        </div>
        <span data-testid="sidebar-words-count" className={`text-[10px] font-mono ${unlimited ? "text-emerald-400" : wEmpty ? "text-red-400" : "text-slate-200"}`}>
          {unlimited ? "∞ Admin" : <>{wRem.toLocaleString()}<span className="text-slate-500">/{wTotal.toLocaleString()}</span></>}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-white/[0.05] overflow-hidden mb-2">
        <div className={`h-full ${wBarClass} transition-all`} style={{ width: `${wPct}%` }}/>
      </div>

      {(empty || wEmpty) ? (
        <p className="text-[11px] text-red-300/90 mb-2">{wEmpty && !empty ? "Parole esaurite" : empty && !wEmpty ? "Crediti esauriti" : "Limiti raggiunti"}</p>
      ) : (low || wLow) ? (
        <p className="text-[11px] text-amber-300/90 mb-2">In esaurimento</p>
      ) : null}
      {!unlimited && (
        <button
          data-testid="sidebar-upgrade-btn"
          onClick={onUpgrade}
          className={`w-full py-1.5 rounded-lg text-xs font-medium transition-all ${(empty || wEmpty || low || wLow)
            ? "bf-btn-gold"
            : "bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white"}`}
        >
          {ctaLabel}
        </button>
      )}
      {unlimited && (
        <p className="text-center text-[10px] text-emerald-300/80 uppercase tracking-wider font-semibold">
          Account Admin — nessun limite
        </p>
      )}
    </div>
  );
}
